import React from "react";
import { CaretDown } from "@phosphor-icons/react";
import { sentenceCase as prettyTitle } from "@/lib/sentenceCase";

/**
 * CatechismAccordionIndex — fully nested table of contents for the
 * Catechism of the Catholic Church (CCC).
 *
 * Hierarchy (matching the Vatican source):
 *   Parte → Sección → Capítulo [accordion] → Artículo [accordion] → Subartículo
 *
 * Behaviour
 * ---------
 *  • Parts and Sections are rendered as plain headers (always visible).
 *  • Chapters are the **outer** accordion: tapping a Chapter expands its
 *    list of Articles inline. At most one Chapter is open at a time.
 *  • Articles are the **inner** accordion: tapping one expands a flat list
 *    of Subarticles (I, II, III…). At most one Article is open per
 *    Chapter.
 *  • Each Subarticle is a clickable row that invokes `onPick(startPara)`
 *    so the parent page can scroll the linear reading view to that
 *    paragraph.
 *
 * Edge cases
 * ----------
 *  • Some Chapters in the CCC have no real Article level — their I/II/III
 *    sit directly under the Chapter. Those are represented in the data
 *    file with an empty-label "synthetic" Article wrapper. We detect that
 *    case and skip the Article accordion entirely, showing the
 *    subarticles directly under the open Chapter.
 *  • Some Articles have no subarticles at all (rare). We still let the
 *    user tap the Article header, which then becomes a direct jump to
 *    that Article's starting paragraph.
 */
export default function CatechismAccordionIndex({ parts, lang, onPick }) {
    const [openChapter, setOpenChapter] = React.useState(null);
    const [openArticle, setOpenArticle] = React.useState(null);
    const chapterRefs = React.useRef({});

    const toggleChapter = React.useCallback((id) => {
        setOpenChapter((cur) => (cur === id ? null : id));
        setOpenArticle(null);
        requestAnimationFrame(() => {
            const el = chapterRefs.current[id];
            if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
    }, []);

    const toggleArticle = React.useCallback((id) => {
        setOpenArticle((cur) => (cur === id ? null : id));
    }, []);

    const partLabel = lang === "es" ? "Parte" : "Part";

    return (
        <div className="flex flex-col gap-8" data-testid="ccc-accordion">
            {parts.filter((p) => p.num > 0).map((part) => (
                <PartBlock
                    key={part.id}
                    part={part}
                    partLabel={partLabel}
                    openChapter={openChapter}
                    openArticle={openArticle}
                    onToggleChapter={toggleChapter}
                    onToggleArticle={toggleArticle}
                    onPick={onPick}
                    chapterRefs={chapterRefs}
                    lang={lang}
                />
            ))}
        </div>
    );
}

function PartBlock({
    part, partLabel, openChapter, openArticle,
    onToggleChapter, onToggleArticle, onPick, chapterRefs, lang,
}) {
    return (
        <section data-testid={`ccc-part-${part.num}`} className="flex flex-col gap-3">
            <header className="flex items-baseline justify-between gap-3 pb-2 border-b border-sand-300">
                <div className="min-w-0">
                    <p className="label-eyebrow mb-1">
                        {partLabel} {romanize(part.num)}
                    </p>
                    <h2 className="reading-serif text-xl sm:text-2xl leading-tight text-stone900">
                        {prettyTitle(part.title)}
                    </h2>
                </div>
            </header>
            {part.sections.map((section) => (
                <SectionBlock
                    key={section.id}
                    section={section}
                    openChapter={openChapter}
                    openArticle={openArticle}
                    onToggleChapter={onToggleChapter}
                    onToggleArticle={onToggleArticle}
                    onPick={onPick}
                    chapterRefs={chapterRefs}
                    lang={lang}
                />
            ))}
        </section>
    );
}

function SectionBlock({
    section, openChapter, openArticle,
    onToggleChapter, onToggleArticle, onPick, chapterRefs, lang,
}) {
    const showHeader = (section.title || "").trim().length > 0;
    return (
        <div data-testid={`ccc-section-${section.id}`} className="flex flex-col gap-2">
            {showHeader && (
                <h3
                    className="ui-sans uppercase tracking-[0.16em] text-[11px] font-semibold text-stoneMuted pt-3 pb-1"
                    data-testid={`ccc-section-title-${section.id}`}
                >
                    {prettyTitle(section.title)}
                </h3>
            )}
            <ol className="flex flex-col gap-1.5" aria-label={section.title || "section"}>
                {section.chapters.map((chapter) => (
                    <ChapterRow
                        key={chapter.id}
                        chapter={chapter}
                        isOpen={openChapter === chapter.id}
                        openArticle={openArticle}
                        onToggleChapter={onToggleChapter}
                        onToggleArticle={onToggleArticle}
                        onPick={onPick}
                        chapterRefs={chapterRefs}
                        lang={lang}
                    />
                ))}
            </ol>
        </div>
    );
}

function ChapterRow({
    chapter, isOpen, openArticle, onToggleChapter, onToggleArticle, onPick, chapterRefs, lang,
}) {
    // A synthetic chapter (empty title, present when a section binds
    // Articles directly without naming a Chapter — e.g. Section 4-2 in
    // EN) is rendered flat: no chapter header, just the articles list.
    const synthetic = !((chapter.title || "").trim());
    if (synthetic) {
        return (
            <li
                ref={(el) => { chapterRefs.current[chapter.id] = el; }}
                style={{ contentVisibility: "auto", containIntrinsicSize: "1px 80px" }}
            >
                <ol className="flex flex-col gap-1.5">
                    {chapter.articles.map((article) => (
                        <ArticleRow
                            key={article.id}
                            article={article}
                            isOpen={openArticle === article.id}
                            onToggleArticle={onToggleArticle}
                            onPick={onPick}
                        />
                    ))}
                </ol>
            </li>
        );
    }

    return (
        <li
            ref={(el) => { chapterRefs.current[chapter.id] = el; }}
            style={{ contentVisibility: "auto", containIntrinsicSize: "1px 80px" }}
        >
            <button
                type="button"
                onClick={() => onToggleChapter(chapter.id)}
                aria-expanded={isOpen}
                aria-controls={`ccc-${chapter.id}-body`}
                data-testid={`ccc-chapter-${chapter.id}`}
                className={[
                    "w-full flex items-center justify-between gap-3",
                    "px-4 py-3.5 rounded-md text-left transition-colors",
                    "ui-sans select-none",
                    isOpen
                        ? "border-2 border-purple-700 bg-purple-50/60"
                        : "border border-sand-300 bg-white hover:border-purple-500",
                ].join(" ")}
            >
                <span className="flex-1 min-w-0">
                    <span className="block reading-serif text-base sm:text-lg leading-tight text-stone900">
                        {prettyTitle(chapter.title)}
                    </span>
                    {(chapter.start || chapter.end) && (
                        <span className="block ui-sans text-xs text-stoneMuted mt-1 tabular-nums">
                            §{chapter.start}{chapter.end && chapter.end !== chapter.start ? `–§${chapter.end}` : ""}
                            {chapter.articles?.length > 0 && (
                                <>
                                    {" · "}
                                    {chapter.articles.length}{" "}
                                    {lang === "es"
                                        ? (chapter.articles.length === 1 ? "artículo" : "artículos")
                                        : (chapter.articles.length === 1 ? "article" : "articles")}
                                </>
                            )}
                        </span>
                    )}
                </span>
                <CaretDown
                    size={14}
                    weight="bold"
                    aria-hidden="true"
                    className={[
                        "shrink-0 text-stoneMuted transition-transform duration-200",
                        isOpen ? "rotate-180" : "",
                    ].join(" ")}
                />
            </button>
            {isOpen && (
                <div
                    id={`ccc-${chapter.id}-body`}
                    role="group"
                    className="mt-2 mb-1 ml-2 pl-2 border-l-2 border-purple-200"
                    data-testid={`ccc-chapter-body-${chapter.id}`}
                >
                    <ol className="flex flex-col gap-1.5">
                        {chapter.articles.map((article) => (
                            <ArticleRow
                                key={article.id}
                                article={article}
                                isOpen={openArticle === article.id}
                                onToggleArticle={onToggleArticle}
                                onPick={onPick}
                            />
                        ))}
                        {chapter.articles.length === 0 && chapter.start && (
                            // Empty-chapter fallback: a direct "go to §N" link.
                            <li>
                                <button
                                    type="button"
                                    onClick={() => onPick(chapter.start)}
                                    data-testid={`ccc-chapter-jump-${chapter.id}`}
                                    className="ui-sans text-sm text-purple-700 hover:underline px-3 py-2"
                                >
                                    {lang === "es" ? "Ir al inicio del capítulo" : "Go to chapter start"} §{chapter.start}
                                </button>
                            </li>
                        )}
                    </ol>
                </div>
            )}
        </li>
    );
}

function ArticleRow({ article, isOpen, onToggleArticle, onPick }) {
    const synthetic = !((article.label || "").trim()) && !((article.title || "").trim());
    // Render subarticles directly when the Article is a synthetic wrapper.
    if (synthetic) {
        return (
            <li>
                <ol className="flex flex-col gap-1">
                    {article.subarticles.map((sub) => (
                        <SubarticleRow key={`${sub.label}-${sub.start}`} sub={sub} onPick={onPick} />
                    ))}
                </ol>
            </li>
        );
    }

    const hasSubs = article.subarticles && article.subarticles.length > 0;
    // Hide entries that cannot do anything: no paragraph to jump to AND no
    // children to expand. These are TOC artefacts (rare — a few Spanish
    // articles whose ranges couldn't be parsed from the source); rendering
    // them as empty accordions only confuses the user.
    if (!hasSubs && !article.start) return null;
    const range = article.start && article.end && article.end > article.start
        ? `${article.start}–${article.end}`
        : (article.start ? `${article.start}` : "");

    return (
        <li>
            <button
                type="button"
                onClick={() => {
                    // When there are no subarticles, the article header
                    // doubles as a direct jump target.
                    if (!hasSubs && article.start) {
                        onPick(article.start);
                        return;
                    }
                    onToggleArticle(article.id);
                }}
                aria-expanded={hasSubs ? isOpen : undefined}
                aria-controls={hasSubs ? `ccc-art-${article.id}-body` : undefined}
                data-testid={`ccc-article-${article.id}`}
                className={[
                    "w-full flex items-center justify-between gap-3",
                    "px-3.5 py-2.5 rounded-md text-left transition-colors",
                    "ui-sans select-none",
                    isOpen
                        ? "border border-purple-500 bg-purple-50/40"
                        : "border border-sand-200 bg-white hover:border-purple-400",
                ].join(" ")}
            >
                <span className="flex-1 min-w-0">
                    <span className="block reading-serif text-[15px] sm:text-base leading-snug text-stone900">
                        {article.label ? <span className="font-semibold">{article.label}: </span> : null}
                        {prettyTitle(article.title)}
                        {range && (
                            <span className="ui-sans font-normal text-stoneMuted tabular-nums ml-1.5">
                                ({range})
                            </span>
                        )}
                    </span>
                </span>
                {hasSubs && (
                    <CaretDown
                        size={12}
                        weight="bold"
                        aria-hidden="true"
                        className={[
                            "shrink-0 text-stoneMuted transition-transform duration-200",
                            isOpen ? "rotate-180" : "",
                        ].join(" ")}
                    />
                )}
            </button>
            {hasSubs && isOpen && (
                <div
                    id={`ccc-art-${article.id}-body`}
                    role="group"
                    className="mt-1.5 mb-1 ml-2 pl-2 border-l border-purple-200"
                    data-testid={`ccc-article-body-${article.id}`}
                >
                    <ol className="flex flex-col gap-1 py-1">
                        {article.subarticles.map((sub) => (
                            <SubarticleRow key={`${sub.label}-${sub.start}`} sub={sub} onPick={onPick} />
                        ))}
                    </ol>
                </div>
            )}
        </li>
    );
}

function SubarticleRow({ sub, onPick }) {
    return (
        <li>
            <button
                type="button"
                onClick={() => onPick(sub.start)}
                data-testid={`ccc-sub-${sub.start}`}
                className={[
                    "w-full text-left px-3 py-2 rounded-md",
                    "ui-sans text-[13.5px] sm:text-sm leading-snug",
                    "text-stone900 hover:bg-purple-50 transition-colors",
                    "flex items-baseline gap-2",
                ].join(" ")}
            >
                <span className="font-semibold text-purple-700 tabular-nums shrink-0 min-w-[1.5rem]">
                    {sub.label}.
                </span>
                <span className="flex-1 min-w-0">
                    {prettyTitle(sub.title)}
                </span>
                <span className="ui-sans text-[11px] text-stoneMuted tabular-nums shrink-0">
                    §{sub.start}
                </span>
            </button>
        </li>
    );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
// `prettyTitle` is re-exported from /lib/sentenceCase.js so the in-page
// HierarchyHeaders banners read identically to the index entries.

function romanize(n) {
    const map = ["", "I", "II", "III", "IV", "V"];
    return map[n] || String(n);
}
